import { QuoteCardType } from 'components/QuoteCard';
import { TQLLogo } from 'icons/TQLLogo';
import { QuickQuoteResponseQuote } from 'lib/api/getQuickQuote';
import { Maybe, Undef } from 'types/UtilityTypes';
import { Quoting } from 'types/enums/Integrations';
import { TruPayQuoteMetadata } from 'types/trupayQuote';

import { QuickQuoteInputs, SelectedQuoteType } from '../types';
import { convertFormStopsToResponseStops } from './formatting';

const positiveOrUndefined = (value: Undef<number>): Undef<number> =>
  value != null && value > 0 ? value : undefined;

export function parseTruPayQuoteCardFromResponse(
  quote: QuickQuoteResponseQuote,
  formValues: QuickQuoteInputs,
  submittedTransportType: string
): Maybe<QuoteCardType> {
  if (quote.source !== Quoting.TruPay || !(quote.rates?.target > 0)) {
    return null;
  }

  const stopCount = Math.max(formValues.stops.length, 2);
  const hasRange =
    quote.metadata?.rangeIsHeuristic !== false &&
    quote.rates.low > 0 &&
    quote.rates.high > 0;
  const distanceMiles = positiveOrUndefined(quote.distance);
  const hasPerMile = distanceMiles != null && quote.rates.targetPerMile > 0;
  const hasRangePerMile =
    hasRange &&
    hasPerMile &&
    quote.rates.lowPerMile > 0 &&
    quote.rates.highPerMile > 0;

  const trupayMetadata: TruPayQuoteMetadata = {
    quoteId: quote.metadata?.quoteID,
    pickupCount: quote.metadata?.pickupCount ?? 1,
    dropCount: quote.metadata?.dropCount ?? stopCount - 1,
    hasRange,
    distanceMiles,
  };

  return {
    type: SelectedQuoteType.TRUPAY,
    title: 'TruPay',
    icon: <TQLLogo compact />,
    cost: quote.rates.target,
    costPerMile: hasPerMile ? quote.rates.targetPerMile : undefined,
    confidence: null,
    priceRange: hasRange
      ? { lowEstimate: quote.rates.low, highEstimate: quote.rates.high }
      : null,
    priceRangePerMile: hasRangePerMile
      ? {
          lowEstimate: quote.rates.lowPerMile,
          highEstimate: quote.rates.highPerMile,
        }
      : null,
    inputtedTransportType: formValues.transportType,
    actualTransportType:
      submittedTransportType as QuickQuoteInputs['transportType'],
    subTransportType: formValues.subTransportType,
    quotingIntegrationName: 'TruPay',
    quoteId: quote.id,
    isMultiStop: formValues.stops.length > 2,
    stops: convertFormStopsToResponseStops(formValues.stops),
    trupayMetadata,
  };
}
